import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, of } from 'rxjs';
import { map } from 'rxjs/operators';

import { ApiService, HttpMethod } from '../common/api.service';
import { ImageAsset } from '../images/image-asset-model';
import { Member } from './member.model';
import { MemberBasicEditModel, MemberFullEditModel } from './member-edit.model';
import { MemberCountWithCategoriesResult } from './member-count-with-categories-result.model';
import { RecentUploadProgress, MemberUploadResult, CategoryUploadResult } from './member-upload-result.model';
import { MyCampApp } from '../common/enumerations';
import { TextMessage } from './text-message.model';
import { MembershipType } from '../organization/membership-type-item.model';
import { MemberNeedingSmsResponse } from './member-needing-sms-response.model';
import { AppStatus } from '../admin/app-status.model';

@Injectable({
    providedIn: 'root'
})
export class MemberApiService extends ApiService
{
    constructor(http: HttpClient)
    {
        super(http);
    }

    getMembers(sortField?: string, sortOrder?: MyCampApp.SortOrder, pageIndex = 0, pageSize = ApiService.defaultPageSize): Observable<Member[]>
    {
        return this.apiRequest(this.getMembersUrl(sortField, sortOrder, pageIndex, pageSize), HttpMethod.Get)
            .pipe(map((coll: any) => coll.Members.map((json: any) => new Member(json))));
    }

    getMember(id: number): Observable<Member>
    {
        return this.apiRequest<any>(this.getMemberUrl(id), HttpMethod.Get).pipe(map(json => new Member(json)));
    }

    searchMembers(searchString: string): Observable<Member[]>
    {
        return this.apiRequest<any>(this.searchMembersUrl(searchString), HttpMethod.Get)
            .pipe(map((json: any) => json.map((json: any) => new Member(json))));
    }

    createMember(model: MemberFullEditModel): Observable<Member>
    {
        return this.apiRequest<any>(this.createMemberUrl(), HttpMethod.Post, JSON.stringify(model))
            .pipe(map(json => new Member(json)));
    }

    updateMember(model: MemberFullEditModel): Observable<Member>
    {
        return this.apiRequest<any>(this.updateMemberFullMemberInfoUrl(model.Id), HttpMethod.Put, JSON.stringify(model))
            .pipe(map(json => new Member(json)));
    }

    updateMemberBasic(model: MemberBasicEditModel): Observable<any>
    {
        return this.apiRequest<any>(this.updateBasicMemberInfoUrl(model.Id), HttpMethod.Put, JSON.stringify(model));
    }

    deleteMember(member: Member, includeChildren: boolean): Observable<any>
    {
        return this.apiRequest<any>(this.deleteMemberUrl(member.Id, includeChildren), HttpMethod.Delete);
    }

    deleteMembers(memberIds: number[], includeChildren: boolean)
    {
        return this.apiRequest<any>(this.deleteMembersUrl, HttpMethod.Delete, JSON.stringify({ MemberIds: memberIds, IncludeChildren: includeChildren }));
    }

    addCategoriesForMembers(memberIds: number[], categoryIds: number[])
    {
        return this.apiRequest<any>(this.addCategoriesForMembersUrl, HttpMethod.Post, JSON.stringify({ MemberIds: memberIds, CategoryIds: categoryIds }));
    }

    deleteCategoriesForMembers(memberIds: number[], categoryIds: number[])
    {
        return this.apiRequest<any>(this.deleteCategoriesForMembersUrl, HttpMethod.Post, JSON.stringify({ MemberIds: memberIds, CategoryIds: categoryIds }));
    }

    setMembershipTypeForMembers(memberIds: number[], membershipTypeId: number)
    {
        return this.apiRequest<any>(this.setMembershipTypeForMembersUrl, HttpMethod.Post, JSON.stringify({ MemberIds: memberIds, MembershipTypeId: membershipTypeId }));
    }

    setCategoriesForMembers(memberIds: number[], categoryIds: number[])
    {
        return this.apiRequest<any>(this.setCategoriesForMembersUrl, HttpMethod.Post, JSON.stringify({ MemberIds: memberIds, CategoryIds: categoryIds }));
    }

    setImageForMember(id: number, url: string): Observable<ImageAsset>
    {
        return this.apiRequest<any>(this.setOrDeleteMemberImageUrl(id), HttpMethod.Post, '{ "Url": "' + url + '"}')
        .pipe(map((json: any) => new ImageAsset(json)));
    }

    deleteImageForMember(id: number): Observable<any>
    {
        return this.apiRequest<any>(this.setOrDeleteMemberImageUrl(id), HttpMethod.Delete);
    }

    getRecentUploadProgress(): Observable<RecentUploadProgress>
    {
        return this.apiRequest<any>(this.getRecentUploadProgressUrl, HttpMethod.Get).pipe(map(json => new RecentUploadProgress(json)));
    }

    uploadMemberList(file: File, overwriteExistingMembers: boolean, continueOnUploadError: boolean, uploadUserTypeId: number): Observable<MemberUploadResult>
    {
        const formData = new FormData();
        formData.append('0', file, file.name);
        return this.apiUploadRequest<any>(this.uploadMemberListUrl(overwriteExistingMembers, continueOnUploadError, uploadUserTypeId), formData, 600)
            .pipe(map(result => new MemberUploadResult(result)));
    }

    uploadCategoryList(file: File, overwriteExistingCategories: boolean, deleteExistingCategories: boolean): Observable<CategoryUploadResult>
    {
        const formData = new FormData();
        formData.append('0', file, file.name);
        return this.apiUploadRequest<any>(this.uploadCategoryListUrl(overwriteExistingCategories, deleteExistingCategories), formData, 600)
            .pipe(map(result => new CategoryUploadResult(result)));
    }

    downloadMemberList(memberIds: number[]): Observable<Response>
    {
        return this.apiPostRequestForCsv<any>(this.downloadMemberListUrl, JSON.stringify({ MemberIds: memberIds }));
    }

    getMemberCountForCategories(categoryIds: number[]): Observable<MemberCountWithCategoriesResult>
    {
        const params = { filter: categoryIds };
        return this.apiRequest<any>(this.getMemberCountForCategoriesUrl, HttpMethod.Put, JSON.stringify(params))
            .pipe(map(json => new MemberCountWithCategoriesResult(json)));
    }

    getConversation(id: number): Observable<TextMessage[]>
    {
        return this.apiRequest<any>(this.getConversationUrl(id), HttpMethod.Get)
            .pipe(map(list => list.map((json: any) => new TextMessage(json))));
    }

    sendSmsToMember(memberId: number, message: string): Observable<TextMessage>
    {
        const json = {
            memberId: memberId,
            message: message,
        };
        return this.apiRequest<any>(this.sendSmsToMemberUrl, HttpMethod.Post, JSON.stringify(json));
    }

    acknowledgeSmsFromMember(memberId: number): Observable<any>
    {
        const json = { memberId: memberId, };
        return this.apiRequest<any>(this.acknowledgeSmsFromMemberUrl, HttpMethod.Post, JSON.stringify(json));
    }

    getMembersNeedingSmsResponse(): Observable<MemberNeedingSmsResponse[]>
    {
        return this.apiRequest(this.getMembersNeedingSmsResponseUrl, HttpMethod.Get)
            .pipe(map((items: any) => items.map((json: any) => new MemberNeedingSmsResponse(json))));
    }

    syncCampDoc(): Observable<Response>
    {
        return this.apiRequest(this.syncCampDocUrl, HttpMethod.Get);
    }

    // =========================================================================================================================================================
    // URLs
    // =========================================================================================================================================================
    /* tslint:disable:member-ordering */
    private get baseMembersApiUrl(): string { return `${this.baseApiUrl}members`; }
    getMembersUrl(sortField?: string, sortOrder?: MyCampApp.SortOrder, pageIndex = 0, pageSize = ApiService.defaultPageSize): string
    {
        return `${this.baseMembersApiUrl}/directory/admin?${this.getListQueryParams('postdate', pageIndex, pageSize, sortField, sortOrder)}`;
    }
    searchMembersUrl(searchString: string): string { return `${this.baseMembersApiUrl}/search?query=${searchString}`; }
    getMemberUrl(id: number) { return `${this.baseMembersApiUrl}/member/${id}`; }
    createMemberUrl() { return this.baseMembersApiUrl; }
    updateMemberFullMemberInfoUrl(id: number) { return `${this.baseMembersApiUrl}/${id}`; }
    updateBasicMemberInfoUrl(id: number) { return `${this.baseMembersApiUrl}/basic/${id}`; }
    deleteMemberUrl(id: number, includeChildren: boolean) { return `${this.baseMembersApiUrl}/${id}?includeChildren=${includeChildren}`; }
    get deleteMembersUrl() { return `${this.baseMembersApiUrl}/deleteMembers`; }
    setOrDeleteMemberImageUrl(id: number) { return `${this.baseMembersApiUrl}/${id}/image`; }
    get getRecentUploadProgressUrl() { return `${this.baseMembersApiUrl}/uploadProgress`; }
    get getMembersNeedingSmsResponseUrl() { return `${this.baseMembersApiUrl}/needsSmsResponse`; }

    uploadMemberListUrl(overwriteExistingMembers: boolean, continueOnUploadError: boolean, uploadUserTypeId: number) {
        return `${this.baseMembersApiUrl}/upload?overwriteExistingMembers=${overwriteExistingMembers}&continueOnUploadError=${continueOnUploadError}&userTypeId=${uploadUserTypeId}`;
    }
    uploadCategoryListUrl(overwriteExistingCategories: boolean, deleteExistingCategories: boolean) {
        return `${this.baseMembersApiUrl}/uploadCategories?overwriteExistingCategories=${overwriteExistingCategories}&deleteExistingCategories=${deleteExistingCategories}`; }
    get downloadMemberListUrl() { return `${this.baseMembersApiUrl}/downloadMemberList`; }
    get getMemberCountForCategoriesUrl() { return `${this.baseMembersApiUrl}/memberCountsForCategories`; }
    getConversationUrl(id: number) { return `${this.baseMembersApiUrl}/conversation/${id}`; }
    get sendSmsToMemberUrl() { return `${this.baseMembersApiUrl}/conversation/sendMessage`; }
    get addCategoriesForMembersUrl() { return `${this.baseMembersApiUrl}/addCategoriesForMembers`; }
    get deleteCategoriesForMembersUrl() { return `${this.baseMembersApiUrl}/deleteCategoriesForMembers`; }
    get setMembershipTypeForMembersUrl() { return `${this.baseMembersApiUrl}/setMembershipTypeForMembers`; }
    get setCategoriesForMembersUrl() { return `${this.baseMembersApiUrl}/setCategoriesForMembers`; }
    get acknowledgeSmsFromMemberUrl() { return `${this.baseMembersApiUrl}/conversation/acknowledgeMessage`; }
    get syncCampDocUrl() { return `${this.baseMembersApiUrl}/syncCampDoc`; }
}
